import { FC, useState } from 'react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogClose,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import DoorIcon from '@/components/icons/DoorIcon';
import CalendarIcon from '@/components/icons/CalendarIcon';
import Clock01Icon from '@/components/icons/Clock01Icon';
import DollarCircleIcon from '@/components/icons/DollarCircleIcon';
import CreditCardIcon from '@/components/icons/CreditCardIcon';
import PackageIcon from '@/components/icons/PackageIcon';
import InformationCircle from '@/components/icons/InformationCircle';
import OrderStatusBar from '@/features/orders/components/OrderStatusBar';
import OrderDetailsElement from './OrderDetailsElement';
import { useOrderDetailsApi } from '@/features/orders/hooks/useOrderDetailsApi';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { PaymentMethod, PaymentStatus } from '@/features/orders/types/orders-dto';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Props = {
	id: string;
	onClose: () => void;
	isMobile?: boolean;
};

const paymentStatusLabelKey: Record<PaymentStatus, string> = {
	[PaymentStatus.Pending]:
		'main.orders.components.details.list.details.list.paymentStatusValues.pending',
	[PaymentStatus.Paid]:
		'main.orders.components.details.list.details.list.paymentStatusValues.paid',
	[PaymentStatus.Failed]:
		'main.orders.components.details.list.details.list.paymentStatusValues.failed',
};

const OrderDetails: FC<Props> = ({ id, onClose, isMobile }) => {
	const [isOpen, setIsOpen] = useState(true);
	const { t } = useTranslation();

	const {
		orderDetailsData,
		retryOrderPayment,
		isRetryingOrderPayment,
	} = useOrderDetailsApi({ id });

	if (!orderDetailsData) return null;

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const paymentStatus =
		orderDetailsData.paymentStatus === null ||
		orderDetailsData.paymentStatus === undefined
			? undefined
			: (orderDetailsData.paymentStatus as PaymentStatus);

	const showPaymentError =
		paymentStatus === PaymentStatus.Failed ||
		!!orderDetailsData.paymentErrorMessage;

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogDescription />
					<DialogTitle>
						{t('main.orders.components.details.title')}
						<DialogClose
							className={cn(
								'ml-auto flex items-center justify-center cursor-pointer',
								isMobile &&
									'size-11 border border-popover-100 rounded-full',
							)}>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
				</DialogHeader>
				<div className='flex flex-col gap-6 overflow-y-auto overflow-x-hidden select-none'>
					<OrderStatusBar status={orderDetailsData.status} />
					<div className='flex flex-col gap-4'>
						<div className='flex flex-col gap-2 text-sm'>
							<h5 className='text-popover-800'>
								{t(
									'main.orders.components.details.list.info.title',
								)}
							</h5>
							<div>
								{orderDetailsData.lines?.map((order) => (
									<OrderDetailsElement
										key={order.id}
										icon={`${order.quantity
											.toString()
											.slice(0, 2)}x`}
										label={order.orderItemName ?? ''}
										value={order.price}
									/>
								))}
							</div>
						</div>
						<div className='flex flex-col gap-2 text-sm'>
							<h5 className='text-popover-800'>
								{t(
									'main.orders.components.details.list.delivery.title',
								)}
							</h5>
							<div>
								<OrderDetailsElement
									icon={DoorIcon}
									value={orderDetailsData.apartmentName ?? ''}
									label={t(
										'main.orders.components.details.list.delivery.list.apartment',
									)}
								/>
								<OrderDetailsElement
									icon={CalendarIcon}
									label={t(
										'main.orders.components.details.list.delivery.list.deliveryDate',
									)}
									value={DateTimeHelper.formatDate(
										orderDetailsData.deliveryDate,
										2,
									)}
								/>
								<OrderDetailsElement
									icon={Clock01Icon}
									label={t(
										'main.orders.components.details.list.delivery.list.deliveryTime',
									)}
									value={DateTimeHelper.formatTime(
										orderDetailsData.deliveryDate,
									)}
								/>
							</div>
						</div>
						<div className='flex flex-col gap-2 text-sm'>
							<h5 className='text-popover-800'>
								{t(
									'main.orders.components.details.list.details.title',
								)}
							</h5>
							<div>
								<OrderDetailsElement
									icon={DollarCircleIcon}
									label={t(
										'main.orders.components.details.list.details.list.total',
									)}
									value={orderDetailsData.totalAmount}
								/>
								<OrderDetailsElement
									icon={CreditCardIcon}
									label={t(
										'main.orders.components.details.list.details.list.paymentMethod',
									)}
									value={
										PaymentMethod[
											orderDetailsData.paymentMethod
										]
									}
								/>
								{paymentStatus !== undefined && (
									<OrderDetailsElement
										icon={CreditCardIcon}
										label={t(
											'main.orders.components.details.list.details.list.paymentStatus',
										)}
										value={t(
											paymentStatusLabelKey[paymentStatus],
										)}
									/>
								)}
								{showPaymentError && (
									<div className='py-3 flex items-start gap-2.5 text-sm text-error-1000'>
										<InformationCircle className='size-5 shrink-0 stroke-error-1000' />
										<span>
											{orderDetailsData.paymentErrorMessage ||
												t(
													'main.orders.components.details.paymentErrorFallback',
												)}
										</span>
									</div>
								)}
								<OrderDetailsElement
									icon={Clock01Icon}
									label={t(
										'main.orders.components.details.list.details.list.timestamp',
									)}
									value={DateTimeHelper.formatDate(
										orderDetailsData.documentDate,
										3,
									)}
								/>
								<OrderDetailsElement
									icon={PackageIcon}
									label={t(
										'main.orders.components.details.list.details.list.orderID',
									)}
									value={orderDetailsData.id}
								/>
							</div>
							{orderDetailsData.canRetryPayment && (
								<Button
									className='mt-2 w-full gap-2'
									disabled={isRetryingOrderPayment}
									onClick={() => retryOrderPayment()}>
									{t(
										'main.orders.components.details.retryPayment',
									)}
								</Button>
							)}
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default OrderDetails;
